/**
 * kb-qa-dialog.tsx — Hộp "Thêm hỏi đáp" / "Sửa hỏi đáp" trong một kho:
 *   Dùng chung bộ ô nhập `QaFields` với Dạy AI (câu hỏi chuẩn, câu tương tự, danh mục, câu trả lời).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useCreateKnowledgeEntry, useUpdateKnowledgeEntry, fetchKnowledgeEntry, type KnowledgeEntry } from '@/hooks/use-knowledge'
import { setKnowledgeEnabled } from '@/hooks/use-ai-train-test'
import { useQueryClient } from '@tanstack/react-query'
import { QaFields } from '../train/qa-form'
import { parseQuestionLines } from '../train/train-lib'

export function KbQaDialog({ baseId, entry, defaultCategoryId = '', onClose }: {
  baseId: string
  /** Có = sửa; không = thêm mới. */
  entry?: KnowledgeEntry | null
  defaultCategoryId?: string
  onClose: () => void
}) {
  const { t } = useTranslation('aiKnow')
  const qc = useQueryClient()
  const create = useCreateKnowledgeEntry()
  const update = useUpdateKnowledgeEntry()
  const [question, setQuestion] = useState(entry?.title ?? '')
  const [similar, setSimilar] = useState((entry?.questions ?? []).join('\n'))
  const [answer, setAnswer] = useState(entry && !entry.contentTruncated ? entry.content : '')
  const [loadingFull, setLoadingFull] = useState(!!entry?.contentTruncated)
  const [categoryId, setCategoryId] = useState(entry ? entry.categoryId ?? '' : defaultCategoryId)
  const [enabled, setEnabled] = useState(entry ? entry.status !== 'archived' : true)
  const busy = create.isPending || update.isPending
  const similarList = parseQuestionLines(similar, question)

  useEffect(() => {
    if (!entry?.contentTruncated) return
    let alive = true
    fetchKnowledgeEntry(entry.id)
      .then((full) => { if (alive) setAnswer(full.content) })
      .catch((err) => toast.error(apiError(err)))
      .finally(() => { if (alive) setLoadingFull(false) })
    return () => { alive = false }
  }, [entry?.id, entry?.contentTruncated])

  async function save() {
    if (!question.trim() || !answer.trim()) { toast.error(t('qaDialog.needQA')); return }
    try {
      if (entry) {
        await update.mutateAsync({
          id: entry.id,
          data: { title: question.trim(), questions: similarList, content: answer.trim(), categoryId: categoryId || null, changeNote: 'Sửa từ Kho tri thức' }, // i18n-ignore: ghi chú phiên bản lưu vào dữ liệu
        })
        if (enabled !== (entry.status !== 'archived')) await setKnowledgeEnabled(entry.id, enabled)
        await qc.invalidateQueries({ queryKey: ['knowledge'] })
        toast.success(t('qaDialog.savedEdit'))
      } else {
        const e = await create.mutateAsync({
          type: 'faq', format: 'qa', risk: 'low', source: 'staff_manual',
          title: question.trim(), questions: similarList, content: answer.trim(),
          knowledgeBaseId: baseId, categoryId: categoryId || null,
        })
        if (!enabled) { await setKnowledgeEnabled(e.id, false); await qc.invalidateQueries({ queryKey: ['knowledge'] }) }
        toast.success(t('qaDialog.savedNew'))
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{entry ? t('qaDialog.titleEdit') : t('qaDialog.titleNew')}</DialogTitle>
          <DialogDescription>{t('qaDialog.description')}</DialogDescription>
        </DialogHeader>
        <QaFields
          {...{ question, setQuestion, similar, setSimilar, answer, setAnswer, categoryId, setCategoryId }}
          knowledgeBaseId={baseId} answerLoading={loadingFull}
        />
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={enabled} onCheckedChange={setEnabled} /> {t('qaDialog.aiUse')}</label>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
            <Button onClick={() => void save()} disabled={busy || loadingFull}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {entry ? t('common:actions.save') : t('common:actions.add')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
